import React, { createContext, useCallback, useContext, useMemo } from 'react';
import type { ViewProps } from 'react-native';

import { client } from './client';
import type { BrazeUser } from './client/types';

// ────────────────────────────────────────────────────────────────────────────────

const { init, destroy, changeUser } = client;

const BrazeContext = createContext<BrazeContextType | undefined>(undefined);

/**
 Braze provider component.
 *
 * @example
 * const { syncCustomer } = useBraze();
 * syncCustomer(customer)
 *
 * @see {@link https://www.braze.com/}
 */
export const BrazeProvider = (props: Pick<ViewProps, 'children'>) => {
  //
  // ─── HELPERS ────────────────────────────────────────────────────────

  const syncCustomer = useCallback((customer: BrazeUser) => {
    if (!customer.id) return;

    changeUser?.(customer);
  }, []);

  // ────────────────────────────────────────────────────────────────────

  const value = useMemo(
    () => ({ init, destroy, syncCustomer }),
    [syncCustomer],
  );

  return (
    <BrazeContext.Provider value={value}>
      {props.children}
    </BrazeContext.Provider>
  );
};

/**
 * Braze client hook.
 *
 * @example
 * const { syncCustomer } = useBraze();
 * syncCustomer('USER_ID')
 *
 * @see {@link https://www.braze.com/}
 */
export const useBraze = () => {
  const context = useContext(BrazeContext);

  if (context === undefined) {
    throw new Error('useBraze must be used within a <BrazeProvider>');
  }

  return context;
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type BrazeContextType = {
  readonly init: (() => void) | undefined;
  readonly destroy: (() => void) | undefined;
  readonly syncCustomer: (user: BrazeUser) => void;
};
